import { useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { getMe, sendProfileOTP, submitProfileUpdateRequest, getMyProfileUpdateRequests } from '../../api/authApi';

const cyan = '#00e5ff';

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'block',
  marginBottom: 6,
};

const inputStyle = (disabled) => ({
  width: '100%',
  background: disabled ? 'rgba(255,255,255,0.03)' : 'rgba(0,229,255,0.04)',
  border: `1px solid ${disabled ? 'rgba(255,255,255,0.06)' : 'rgba(0,229,255,0.2)'}`,
  borderRadius: 8,
  padding: '10px 14px',
  color: disabled ? '#555' : '#fff',
  fontSize: 14,
  outline: 'none',
  cursor: disabled ? 'not-allowed' : 'text',
});

const COUNTRY_CODES = {
  'United States': '+1', 'Canada': '+1', 'India': '+91', 'United Kingdom': '+44',
  'Germany': '+49', 'France': '+33', 'Australia': '+61', 'Japan': '+81',
  'China': '+86', 'Brazil': '+55', 'Mexico': '+52', 'Singapore': '+65',
  'Hong Kong': '+852', 'Thailand': '+66', 'Indonesia': '+62', 'Philippines': '+63',
  'Pakistan': '+92', 'Bangladesh': '+880', 'Vietnam': '+84', 'South Africa': '+27',
  'Nigeria': '+234', 'Kenya': '+254', 'United Arab Emirates': '+971',
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', mobile: '', country: '', dateOfBirth: '', address: '', usdtWalletAddress: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  // OTP step
  const [otpStep, setOtpStep] = useState('form'); // 'form' | 'otp'
  const [otp, setOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [canResendOtp, setCanResendOtp] = useState(false);

  useEffect(() => {
    Promise.all([
      getMe().then((res) => {
        const u = res.data.user;
        setUser(u);
        setForm({
          name: u.name || '',
          email: u.email || '',
          mobile: u.mobile || '',
          country: u.country || '',
          dateOfBirth: u.dateOfBirth ? u.dateOfBirth.split('T')[0] : '',
          address: u.address || '',
          usdtWalletAddress: u.usdtWalletAddress || '',
        });
      }),
      getMyProfileUpdateRequests().then((res) => {
        setPendingRequests(res.data.requests.filter(r => r.status === 'pending'));
      }).catch(() => {}),
    ])
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setSuccess('');
    setError('');
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }
  };

  useEffect(() => {
    if (otpTimer > 0) {
      const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(t);
    }
    if (otpTimer === 0 && otpStep === 'otp') setCanResendOtp(true);
  }, [otpTimer, otpStep]);

  const isFilled = (v) => v !== null && v !== undefined && String(v).trim() !== '';
  const fieldDisabled = (key) => !editMode || isFilled(user?.[key]);

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required.';
    if (!form.email.trim()) errors.email = 'Email is required.';
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.email = 'Invalid email format.';
    if (!form.mobile.trim()) errors.mobile = 'Mobile is required.';
    if (form.mobile.length < 10) errors.mobile = 'Mobile must be at least 10 digits.';
    if (form.usdtWalletAddress.trim() && !form.usdtWalletAddress.match(/^(0x[a-fA-F0-9]{40}|T[1-9A-HJ-NP-Z]{32,33})$/)) {
      errors.usdtWalletAddress = 'Invalid USDT address format.';
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setOtpSending(true);
    setError('');
    setOtpError('');
    try {
      await sendProfileOTP();
      setOtpStep('otp');
      setOtp('');
      setOtpTimer(30);
      setCanResendOtp(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      await sendProfileOTP();
      setOtpTimer(30);
      setCanResendOtp(false);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyAndSubmit = async () => {
    if (!otp.trim() || otp.length !== 6) { setOtpError('Please enter a valid 6-digit OTP.'); return; }
    setSaving(true);
    setOtpError('');
    try {
      await submitProfileUpdateRequest({ ...form, otp });
      setSuccess('✓ Profile update request submitted! Awaiting admin approval.');
      setEditMode(false);
      setOtpStep('form');
      setOtp('');
      const res = await getMyProfileUpdateRequests();
      setPendingRequests(res.data.requests.filter(r => r.status === 'pending'));
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 700 }}>
        {/* Pending Requests Alert */}
        {pendingRequests.length > 0 && (
          <div style={{ background: 'rgba(255,170,68,0.12)', border: '1px solid rgba(255,170,68,0.3)', color: '#ffaa44', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
            <strong>⏳ {pendingRequests.length} profile update{pendingRequests.length > 1 ? 's' : ''} pending admin approval</strong>
            <div style={{ fontSize: 12, marginTop: 4, color: '#ffcc99' }}>You'll be notified once approved or rejected.</div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h5 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>My Profile</h5>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              style={{
                background: cyan,
                border: 'none',
                color: '#000',
                borderRadius: 6,
                padding: '6px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Edit Profile
            </button>
          )}
        </div>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 28 }}>
          {editMode ? 'Update your personal and banking information. Changes require admin approval.' : 'Your profile information'}
        </div>

        <div style={{
          background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
          border: '1px solid rgba(0,229,255,0.15)',
          borderRadius: 14,
          padding: '28px 24px',
        }}>
          {error && (
            <div style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ background: 'rgba(102,255,153,0.1)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
              {success}
            </div>
          )}

          <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Personal Info Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ color: '#00e5ff', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 14, letterSpacing: '0.08em' }}>Personal Information</div>
            </div>

            {/* Name */}
            <div>
              <label style={labelStyle}>Full Name</label>
              <input name="name" type="text" value={form.name} onChange={handleChange} disabled={fieldDisabled('name')}
                style={{ ...inputStyle(fieldDisabled('name')), ...(validationErrors.name && { borderColor: '#ff6666' }) }}
                placeholder="Your full name" />
              {validationErrors.name && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>{validationErrors.name}</div>}
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email Address</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} disabled={fieldDisabled('email')}
                style={{ ...inputStyle(fieldDisabled('email')), ...(validationErrors.email && { borderColor: '#ff6666' }) }}
                placeholder="your@email.com" />
              {validationErrors.email && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>{validationErrors.email}</div>}
            </div>

            {/* Mobile */}
            <div>
              <label style={labelStyle}>Mobile Number</label>
              <input name="mobile" type="tel" value={form.mobile} onChange={handleChange} disabled={fieldDisabled('mobile')}
                style={{ ...inputStyle(fieldDisabled('mobile')), ...(validationErrors.mobile && { borderColor: '#ff6666' }) }}
                placeholder="Your mobile number" />
              {validationErrors.mobile && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>{validationErrors.mobile}</div>}
            </div>

            {/* Country */}
            <div>
              <label style={labelStyle}>Country</label>
              <select name="country" value={form.country} onChange={handleChange} disabled={fieldDisabled('country')}
                style={{ ...inputStyle(fieldDisabled('country')), cursor: fieldDisabled('country') ? 'not-allowed' : 'pointer' }}>
                <option value="">Select country</option>
                {Object.keys(COUNTRY_CODES).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Date of Birth */}
            <div>
              <label style={labelStyle}>Date of Birth</label>
              <input name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={handleChange} disabled={fieldDisabled('dateOfBirth')}
                style={inputStyle(fieldDisabled('dateOfBirth'))} />
            </div>

            {/* Address */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <input name="address" type="text" value={form.address} onChange={handleChange} disabled={fieldDisabled('address')}
                style={inputStyle(fieldDisabled('address'))} placeholder="Your residential address" />
            </div>

            {/* USDT Address */}
            <div style={{ gridColumn: '1 / -1', marginTop: 10 }}>
              <label style={labelStyle}>Wallet Address ( USDT - BEP20)</label>
              <input name="usdtWalletAddress" type="text" value={form.usdtWalletAddress} onChange={handleChange} disabled={fieldDisabled('usdtWalletAddress')}
                style={{ ...inputStyle(fieldDisabled('usdtWalletAddress')), fontFamily: 'monospace', fontSize: '12px', ...(validationErrors.usdtWalletAddress && { borderColor: '#ff6666' }) }}
                placeholder="e.g. 0x... (BEP-20)" />
              {validationErrors.usdtWalletAddress && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>{validationErrors.usdtWalletAddress}</div>}
            </div>

            {/* Buttons / OTP Step */}
            {editMode && (
              <div style={{ gridColumn: '1 / -1', marginTop: 10 }}>
                {otpStep === 'otp' ? (
                  <div style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 10, padding: '20px' }}>
                    <div style={{ color: '#aaa', fontSize: 13, marginBottom: 14 }}>
                      A 6-digit OTP has been sent to your email. Enter it below to confirm your profile update request.
                    </div>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                      placeholder="000000"
                      maxLength="6"
                      autoFocus
                      style={{ width: '100%', background: 'rgba(0,229,255,0.04)', border: `1px solid ${otpError ? '#ff6666' : 'rgba(0,229,255,0.2)'}`, borderRadius: 8, padding: '12px 14px', color: '#fff', fontSize: 26, fontWeight: 700, letterSpacing: 10, textAlign: 'center', outline: 'none', marginBottom: 8 }}
                    />
                    {otpTimer > 0 && (
                      <div style={{ color: '#555', fontSize: 11, textAlign: 'center', marginBottom: 8 }}>Resend in {otpTimer}s</div>
                    )}
                    {otpError && (
                      <div style={{ color: '#ff6666', fontSize: 12, marginBottom: 10 }}>{otpError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="button" onClick={() => { setOtpStep('form'); setOtp(''); setOtpError(''); }}
                        style={{ flex: 1, padding: '11px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#888', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                        ← Back
                      </button>
                      <button type="button" onClick={handleResendOTP} disabled={!canResendOtp || otpSending}
                        style={{ flex: 1, padding: '11px', borderRadius: 8, border: '1px solid rgba(0,229,255,0.2)', background: canResendOtp ? 'rgba(0,229,255,0.08)' : 'rgba(255,255,255,0.03)', color: canResendOtp ? cyan : '#444', fontWeight: 600, fontSize: 13, cursor: canResendOtp ? 'pointer' : 'not-allowed' }}>
                        Resend OTP
                      </button>
                      <button type="button" onClick={handleVerifyAndSubmit} disabled={otp.length !== 6 || saving}
                        style={{ flex: 2, padding: '11px', borderRadius: 8, border: 'none', background: otp.length !== 6 || saving ? 'rgba(255,255,255,0.06)' : cyan, color: otp.length !== 6 || saving ? '#555' : '#000', fontWeight: 700, fontSize: 13, cursor: otp.length !== 6 || saving ? 'not-allowed' : 'pointer' }}>
                        {saving ? 'Submitting...' : 'Confirm & Submit →'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button type="button" onClick={() => { setEditMode(false); setValidationErrors({}); setOtpStep('form'); }}
                      style={{ flex: 1, padding: '12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#888', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={otpSending}
                      style={{ flex: 1, padding: '12px', borderRadius: 8, border: 'none', background: otpSending ? 'rgba(255,255,255,0.06)' : cyan, color: otpSending ? '#555' : '#000', fontWeight: 700, fontSize: 14, cursor: otpSending ? 'not-allowed' : 'pointer' }}>
                      {otpSending ? 'Sending OTP...' : 'Submit for Approval'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </Container>
    </div>
  );
};

export default Profile;
